import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface ThreeDSMetadata {
  accessToken: string;
  expiresInSeconds: number;
  createdAt: Date;
  hasAlreadyAuthenticated: boolean;
}

interface CieloContextData {
  threeDSMetadata: ThreeDSMetadata | null;
  setThreeDSMetadata: (metadata: ThreeDSMetadata | null) => void;
  clearThreeDSMetadata: () => void;
}

const CieloContext = createContext<CieloContextData | undefined>(undefined);

interface CieloProviderProps {
  children: React.ReactNode;
}

export function CieloProvider({ children }: CieloProviderProps) {
  const [threeDSMetadata, setThreeDSMetadataState] = useState<ThreeDSMetadata | null>(null);

  const setThreeDSMetadata = useCallback((metadata: ThreeDSMetadata | null) => {
    setThreeDSMetadataState(metadata);
  }, []);

  const clearThreeDSMetadata = useCallback(() => {
    setThreeDSMetadataState(null);
  }, []);

  const value: CieloContextData = useMemo(
    () => ({
      threeDSMetadata,
      setThreeDSMetadata,
      clearThreeDSMetadata,
    }),
    [threeDSMetadata, setThreeDSMetadata, clearThreeDSMetadata]
  );

  return <CieloContext.Provider value={value}>{children}</CieloContext.Provider>;
}

export function useCielo() {
  const context = useContext(CieloContext);
  if (context === undefined) {
    throw new Error('useCielo must be used within a CieloProvider');
  }
  return context;
}
